import { useEffect, useRef, useState } from 'react'
import { isOrgAdmin } from '@assessments/shared'
import { useSession } from '../../features/auth/SessionContext.js'

interface Props {
  /** Platform origin the account links resolve against. Absent → only Logout is shown. */
  platformOrigin?: string
}

const menuItem = 'block px-4 py-2 text-sm text-body no-underline hover:bg-surface-hover'

/**
 * Avatar menu for the global nav, matching the Platform user-dropdown (ethics-quotient): a
 * round avatar button that toggles a right-aligned menu. Account is always shown; My Organization
 * and Manage Users are org-admin only. Logout runs the session logout rather than a form post.
 */
export function UserMenu({ platformOrigin }: Props) {
  const { session, logout } = useSession()
  const [open, setOpen] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const orgAdmin = isOrgAdmin(session?.user.roles)
  const accountUrl = (path: string) => (platformOrigin ? `${platformOrigin}${path}` : undefined)

  async function handleLogout() {
    setLoggingOut(true)
    await logout()
  }

  return (
    <div ref={containerRef} className="relative ml-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open user menu"
        className="flex items-center rounded-full bg-surface focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2"
      >
        <img
          className="h-8 w-8 rounded-full"
          src="/img/user-placeholder.svg"
          alt="Profile"
          width={32}
          height={32}
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-orientation="vertical"
          className="absolute right-0 z-50 mt-2 w-48 origin-top-right divide-y divide-border rounded-md bg-surface font-inter shadow-panel"
        >
          <div className="py-1" role="none">
            {accountUrl('/account/profile') && (
              <a href={accountUrl('/account/profile')} role="menuitem" className={menuItem}>
                Account
              </a>
            )}
            {orgAdmin && accountUrl('/account/my-organization') && (
              <a href={accountUrl('/account/my-organization')} role="menuitem" className={menuItem}>
                My Organization
              </a>
            )}
            {orgAdmin && accountUrl('/account/users') && (
              <a href={accountUrl('/account/users')} role="menuitem" className={menuItem}>
                Manage Users
              </a>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={() => void handleLogout()}
              disabled={loggingOut}
              className={`${menuItem} w-full cursor-pointer border-none bg-transparent text-left disabled:opacity-60`}
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
